import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Editor from 'react-simple-code-editor';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
   handleVariableDrop,
   handleDragOver,
   isVariableDrag,
} from '../../services/dragDrop';

// --- Component
interface TextareaProps {
   keyRef?: string;
   otherKey?: any;
   title?: string;
   value?: string;
   placeholder?: string;
   handleChange?: (e: any, keyRef?: string, otherKey?: any) => void;
   onVariableDrop?: (newValue: string) => void;
}

export function Textarea({
   keyRef,
   otherKey,
   title,
   value,
   placeholder,
   handleChange,
   onVariableDrop,
}: TextareaProps) {
   const [isDragOver, setIsDragOver] = useState(false);
   const [code, setCode] = useState(value || '');
   const editorContainerRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      setCode(value || '');
   }, [value]);

   // Force sync scroll on mount and updates
   useEffect(() => {
      const syncScroll = () => {
         const container = editorContainerRef.current;
         if (!container) return;

         const textarea = container.querySelector('textarea');
         const pre = container.querySelector('pre');

         if (textarea && pre) {
            textarea.scrollTop = container.scrollTop;
            textarea.scrollLeft = container.scrollLeft;
            pre.scrollTop = container.scrollTop;
            pre.scrollLeft = container.scrollLeft;
         }
      };

      // Sync immediately and after a brief delay
      syncScroll();
      const timeout = setTimeout(syncScroll, 100);

      return () => clearTimeout(timeout);
   }, [code]);

   const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const textarea = container.querySelector('textarea');
      const pre = container.querySelector('pre');

      if (textarea && pre) {
         textarea.scrollTop = container.scrollTop;
         textarea.scrollLeft = container.scrollLeft;
         pre.scrollTop = container.scrollTop;
         pre.scrollLeft = container.scrollLeft;
      }
   }, []);

   const handleCodeChange = (newCode: string) => {

      setCode(newCode);

      if (onVariableDrop) {
         onVariableDrop(newCode);
      } else if (handleChange) {
         const syntheticEvent = {
            target: { value: newCode },
            currentTarget: { value: newCode },
         } as React.ChangeEvent<HTMLTextAreaElement>;
         handleChange(syntheticEvent, keyRef, otherKey);
      }
   };

   const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      handleVariableDrop(e, code, (newValue) => handleCodeChange(newValue));
   };

   const handleDragEnterCustom = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (isVariableDrag(e)) {
         e.preventDefault();
         setIsDragOver(true);
         const textarea = e.currentTarget.querySelector("textarea");
         textarea?.classList.add("dragging");
      }
   }, []);

   const handleDragLeaveCustom = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      setIsDragOver(false)
      const textarea = e.currentTarget.querySelector("textarea");
      textarea?.classList.remove("dragging");
   }, []);

   const handleDragOverCustom = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      if (!isVariableDrag(e)) return;
      e.preventDefault();

      const textarea = e.currentTarget.querySelector("textarea") as HTMLTextAreaElement;
      if (!textarea) return;

      // Use the actual plain text value from the textarea
      const text = textarea.value;
      const style = window.getComputedStyle(textarea);
      const font = `${style.fontSize} ${style.fontFamily}`;
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.font = font;

      // Get the cursor position relative to the textarea
      const rect = textarea.getBoundingClientRect();
      const x = e.clientX - rect.left + textarea.scrollLeft;
      const y = e.clientY - rect.top + textarea.scrollTop;

      const availableWidth = textarea.clientWidth - parseFloat(style.paddingLeft || "0") - parseFloat(style.paddingRight || "0");

      // --- Step 1: split text into "visual" lines (respecting wrap width)
      const physicalLines: { text: string; start: number }[] = [];
      let currentLine = "";
      let currentWidth = 0;
      let globalIndex = 0;

      const pushLine = () => {
         physicalLines.push({ text: currentLine, start: globalIndex - currentLine.length });
         currentLine = "";
         currentWidth = 0;
      };

      // Pre-calculate variable positions for accurate measurement
      const variableRegex = /\{\{\s*[^}]+\s*\}\}/g;
      let variableMatches: { start: number; end: number; text: string }[] = [];
      let match;
      while ((match = variableRegex.exec(text)) !== null) {
         variableMatches.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0]
         });
      }

      for (let i = 0; i < text.length; i++) {
         const ch = text[i];

         // Check if we're at the start of a variable
         const currentVariable = variableMatches.find(v => v.start === i);
         if (currentVariable) {
            // Measure the entire variable as a single unit
            const variableWidth = ctx.measureText(currentVariable.text).width;

            if (currentWidth + variableWidth > availableWidth && currentLine.length > 0) {
               pushLine();
            }

            currentLine += currentVariable.text;
            currentWidth += variableWidth;
            i = currentVariable.end - 1; // Skip ahead to the end of the variable
            globalIndex += currentVariable.text.length;
            continue;
         }

         if (ch === "\n") {
            pushLine();
            globalIndex++;
            continue;
         }

         const w = ctx.measureText(ch).width;
         if (currentWidth + w > availableWidth && currentLine.length > 0) {
            pushLine();
         }

         currentLine += ch;
         currentWidth += w;
         globalIndex++;
      }

      if (currentLine.length > 0) {
         pushLine();
      }

      if (text.endsWith("\n")) {
         physicalLines.push({ text: "", start: text.length });
      }

      // --- Step 2: determine which "visual line" mouse is on
      const lineIndex = Math.max(0, Math.min(Math.floor(y / lineHeight), physicalLines.length - 1));
      const targetLine = physicalLines[lineIndex];
      if (!targetLine) return;

      // --- Step 3: determine horizontal character position within line
      let widthAccum = 0;
      let charOffsetInLine = targetLine.text.length;

      // Re-measure the target line accounting for variables
      let measuredIndex = 0;
      for (let j = 0; j < targetLine.text.length; j++) {
         const remainingText = targetLine.text.substring(j);
         const variableMatch = variableMatches.find(v =>
            v.start >= targetLine.start + j &&
            v.start < targetLine.start + j + v.text.length
         );

         let chWidth: number;
         let charsToSkip = 1;

         if (variableMatch && targetLine.text.startsWith(variableMatch.text, j)) {
            // It's a variable - measure it as a whole
            chWidth = ctx.measureText(variableMatch.text).width;
            charsToSkip = variableMatch.text.length;
         } else {
            // Regular character
            chWidth = ctx.measureText(targetLine.text[j]).width;
         }

         if (widthAccum + chWidth / 2 >= x) {
            charOffsetInLine = j;
            break;
         }

         widthAccum += chWidth;
         j += charsToSkip - 1; // Skip the rest of the variable
      }

      const absoluteCaretIndex = targetLine.start + charOffsetInLine;

      textarea.focus();
      textarea.setSelectionRange(absoluteCaretIndex, absoluteCaretIndex);
   }, []);


   const availableVariables = useSelector(
      (state: RootState) => state.variables.variableDetails.map((item) => item.path)
   );

   const highlightVariables = (code: string, availableVariables: string[] = []): string => {
      const formattedCode = code.replace(/&/g, '&amp;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;');
      if (!availableVariables.length) return formattedCode;

      const variableRegex = /\{\{\s*[^}]+\s*\}\}/g;

      return formattedCode.replace(variableRegex, (match) => {
         const cleanMatch = match.replace(/\s+/g, ' ').trim();

         if (availableVariables.includes(cleanMatch)) {
            return `<span class="token variable-highlight${match.includes("$block") ? "-block" : match.includes("$global") ? "-global" : "-workflow"}">${match}</span>`;
         }

         return match;
      });
   };

   return (
      <Main className="textarea">
         {title ? <span className="title">{title}</span> : null}
         <EditorWrapper
            ref={editorContainerRef}
            $isDragOver={isDragOver}
            onScroll={handleScroll}
         >
            <StyledEditor
               value={code}
               onValueChange={handleCodeChange}
               highlight={(text) => highlightVariables(text, availableVariables)}
               padding={10}
               placeholder={placeholder}
               tabSize={4}
               insertSpaces
               onDrop={handleDrop}
               onDragOver={handleDragOverCustom}
               onDragEnter={handleDragEnterCustom}
               onDragLeave={handleDragLeaveCustom}
               style={{
                  fontFamily: 'monospace',
                  fontSize: 14,
                  lineHeight: '1.5',
               }}
            />
         </EditorWrapper>
      </Main>
   );
}

const Main = styled.div`
   position: relative;

   .title {
      position: absolute;
      top: calc(-35px / 2);
      left: 7.5px;
      display: flex;
      align-items: center;
      background-color: #F6F6F6;
      border: 1px dashed rgb(200, 200, 200);
      padding: 0 7.5px;
      height: 25px;
      border-radius: 5px;
      font-size: 16px;
      z-index: 99;
  }
`;

// Main wrapper that handles ALL scrolling
const EditorWrapper = styled.div<{ $isDragOver: boolean }>`
  width: 300px;
  height: 150px;
   min-width: 100%;
   max-width: 500px;
  min-height: 150px;
  max-height: 300px;
  border-radius: 10px;
  border: 1px solid ${({ $isDragOver }) => ($isDragOver ? '#3b82f6' : '#e5e7eb')};
  background-color: #f9fafb;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  resize: both;
  overflow: auto !important;
  position: relative;

  /* Force scrollbar visibility */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 0 10px 10px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// Styled Editor - disable ALL internal scrolling
const StyledEditor = styled(Editor)`
  width: 100%;
  min-height: 100%;
  background: transparent;

  /* The main container div from react-simple-code-editor */
  & > div {
    height: 100%;
    min-height: 100%;
    overflow: hidden !important;
    position: relative;
  }

  /* Textarea - fill container but prevent scrolling */
  textarea {
    width: 100% !important;
    height: 100% !important;
    min-height: 100% !important;
    overflow: hidden !important;
    resize: none !important;
    background: transparent !important;
    color: transparent !important;
    caret-color: #000 !important;
    font-size: 14px !important;
    font-family: monospace !important;
    line-height: 1.5 !important;
    border: none !important;
    outline: none !important;
    padding: 10px !important;
    box-sizing: border-box !important;
    position: absolute;
    top: 0;
    left: 0;
  }

  /* Pre element - fill container but prevent scrolling */
  pre {
    width: 100% !important;
    height: 100% !important;
    min-height: 100% !important;
    overflow: hidden !important;
    margin: 0 !important;
    padding: 10px !important;
    box-sizing: border-box !important;
    background: transparent !important;
    white-space: pre-wrap !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    font-size: 14px !important;
    font-family: monospace !important;
    line-height: 1.5 !important;
    color: #111827 !important;
    position: relative;
    z-index: 1;
  }
  
.variable-highlight-block {
      color: #10b981 !important;
      font-family: monospace !important;
      background-color: rgba(16, 185, 129, 0.09);
      padding: 2px 0;
   }
   .variable-highlight-global {
      color: #b91010ff !important;
      font-family: monospace !important;
      background-color: rgba(185, 16, 16, 0.06);
      padding: 2px 0;
   }
   .variable-highlight-workflow {
      color: #11a5c0 !important;
      font-family: monospace !important;
      background-color: rgba(17, 165, 192, 0.09);
      padding: 2px 0;
   }


  *::selection {
    background: rgba(59, 130, 246, 0.2) !important;
  }
`;